import { Box, LinearProgress, Paper, TableBody, TableCell, TableHead } from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import DataTable, {
    formatDateStandard,
    StyledHeaderTableRow,
    StyledPrimaryTableCell,
    StyledSecondaryTableCell,
    StyledTableRow,
    StyledTableSortLabel,
} from "@/components/Table/DataTable.tsx";
import SearchSection from "@/components/Table/SearchSection";
import { useQueryOptionsFactory } from "@/queries";
import type { ListReservation } from "@/queries/reservations.ts";

type ReservationSortField = keyof Pick<
    ListReservation,
    "startDate" | "endDate" | "customerName" | "carLicense" | "calendar" | "rentalId"
>;

export const Route = createFileRoute("/admin/reservations/")({
    component: RouteComponent,
    pendingComponent: LinearProgress,
    loader: async ({ context }) => {
        await context.queryClient.ensureQueryData(context.qof.reservations.list());
    },
});

function RouteComponent() {
    const qof = useQueryOptionsFactory();
    const pageData: { data: ListReservation[] } = useSuspenseQuery(qof.reservations.list()).data;

    const [sortField, setSortField] = useState<ReservationSortField>("startDate");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
    const [searchTerm, setSearchTerm] = useState("");
    const [displayedData, setDisplayedData] = useState<ListReservation[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(0);
    const ITEMS_PER_PAGE = 50;

    const handleSort = (field: ReservationSortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection(field === "startDate" || field === "endDate" ? "desc" : "asc");
        }
        setPage(0);
        setDisplayedData([]);
        setHasMore(true);
    };

    const formatDateRange = (startDate: Date, endDate: Date) => {
        const start = formatDateStandard(startDate);
        const end = formatDateStandard(endDate);
        return `${start} - ${end}`;
    };

    const getSortedAndFilteredData = useCallback(() => {
        const filteredData = pageData.data.filter((reservation) => {
            const searchLower = searchTerm.toLowerCase();
            return (
                reservation.calendar.owner.ownerName.toLowerCase().includes(searchLower) ||
                reservation.rentalId.toLowerCase().includes(searchLower) ||
                reservation.customerName.toLowerCase().includes(searchLower) ||
                reservation.carLicense.toLowerCase().includes(searchLower)
            );
        });

        return filteredData.sort((a, b) => {
            let aValue: string | Date | ListReservation["calendar"] | number = a[sortField];
            let bValue: string | Date | ListReservation["calendar"] | number = b[sortField];

            if (sortField === "calendar") {
                aValue = a[sortField].owner.ownerName;
                bValue = b[sortField].owner.ownerName;
            }

            if (sortField === "rentalId") {
                aValue = Number.parseInt(aValue?.toString() ?? "0");
                bValue = Number.parseInt(bValue?.toString() ?? "0");
            }

            if (aValue < bValue) {
                return sortDirection === "asc" ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === "asc" ? 1 : -1;
            }
            return 0;
        });
    }, [searchTerm, sortField, sortDirection, pageData.data]);

    const loadMoreData = useCallback(async () => {
        if (isLoading || !hasMore) return;

        setIsLoading(true);

        const sortedAndFilteredData = getSortedAndFilteredData();
        const startIndex = page * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        const newData = sortedAndFilteredData.slice(startIndex, endIndex);

        if (newData.length === 0 || endIndex >= sortedAndFilteredData.length) {
            setHasMore(false);
        }

        if (page === 0) {
            setDisplayedData(newData);
        } else {
            setDisplayedData((prev) => [...prev, ...newData]);
        }

        setPage((prev) => prev + 1);
        setIsLoading(false);
    }, [page, isLoading, hasMore, getSortedAndFilteredData]);

    useEffect(() => {
        setPage(0);
        setDisplayedData([]);
        setHasMore(true);
    }, [searchTerm, sortField, sortDirection]);

    useEffect(() => {
        if (displayedData.length === 0 && hasMore) loadMoreData();
    }, [displayedData.length, hasMore, loadMoreData]);

    return (
        <Box sx={{ maxWidth: "100%" }}>
            <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
                <PageHeader
                    title="Find Reservation - Search All Bookings"
                    instructions={[
                        "Search for reservations by dates, owner name, unit number, guests name, or car license",
                        "Sort by clicking on column headings",
                        "View reservation details including date ranges and car license information",
                    ]}
                />

                <SearchSection
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    onClearSearch={() => setSearchTerm("")}
                    placeholder="Search by dates, owner name, unit number, guest name, or car license..."
                    displayedCount={displayedData.length}
                    totalCount={getSortedAndFilteredData().length}
                    totalAllCount={pageData.data.length}
                />

                <DataTable
                    isLoading={isLoading}
                    hasMore={hasMore}
                    onLoadMore={loadMoreData}
                    isEmpty={displayedData.length === 0}
                    emptyMessage={
                        searchTerm
                            ? "No reservations found matching your search."
                            : "No reservations available."
                    }
                    colSpan={5}
                    maxHeight={{ xs: "600px", sm: "600px" }}
                    loadingMessage="Loading more reservations..."
                    endMessage="No more reservations to load"
                >
                    <TableHead>
                        <StyledHeaderTableRow>
                            <TableCell sx={{ minWidth: { xs: "120px", sm: "160px" } }}>
                                <StyledTableSortLabel
                                    active={sortField === "startDate"}
                                    direction={sortField === "startDate" ? sortDirection : "asc"}
                                    onClick={() => handleSort("startDate")}
                                >
                                    From - To
                                </StyledTableSortLabel>
                            </TableCell>

                            <TableCell sx={{ minWidth: { xs: "100px", sm: "120px" } }}>
                                <StyledTableSortLabel
                                    active={sortField === "calendar"}
                                    direction={sortField === "calendar" ? sortDirection : "asc"}
                                    onClick={() => handleSort("calendar")}
                                >
                                    Owner Name
                                </StyledTableSortLabel>
                            </TableCell>

                            <TableCell sx={{ minWidth: { xs: "60px", sm: "80px" } }}>
                                <StyledTableSortLabel
                                    active={sortField === "rentalId"}
                                    direction={sortField === "rentalId" ? sortDirection : "asc"}
                                    onClick={() => handleSort("rentalId")}
                                >
                                    Unit Number
                                </StyledTableSortLabel>
                            </TableCell>

                            <TableCell sx={{ minWidth: { xs: "100px", sm: "140px" } }}>
                                <StyledTableSortLabel
                                    active={sortField === "customerName"}
                                    direction={sortField === "customerName" ? sortDirection : "asc"}
                                    onClick={() => handleSort("customerName")}
                                >
                                    Guest Name
                                </StyledTableSortLabel>
                            </TableCell>

                            <TableCell sx={{ minWidth: { xs: "80px", sm: "100px" } }}>
                                <StyledTableSortLabel
                                    active={sortField === "carLicense"}
                                    direction={sortField === "carLicense" ? sortDirection : "asc"}
                                    onClick={() => handleSort("carLicense")}
                                >
                                    Car License
                                </StyledTableSortLabel>
                            </TableCell>
                        </StyledHeaderTableRow>
                    </TableHead>

                    <TableBody>
                        {displayedData.map((reservation) => (
                            <StyledTableRow key={reservation.id}>
                                <StyledPrimaryTableCell
                                    sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                                >
                                    {formatDateRange(reservation.startDate, reservation.endDate)}
                                </StyledPrimaryTableCell>

                                <StyledSecondaryTableCell>
                                    {reservation.calendar.owner.ownerFirstName}{" "}
                                    {reservation.calendar.owner.ownerName}
                                </StyledSecondaryTableCell>

                                <StyledPrimaryTableCell>
                                    {reservation.rentalId}
                                </StyledPrimaryTableCell>

                                <StyledPrimaryTableCell>
                                    {reservation.customerFirstName} {reservation.customerName}
                                </StyledPrimaryTableCell>

                                <StyledSecondaryTableCell>
                                    {reservation.carLicense}
                                </StyledSecondaryTableCell>
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </DataTable>
            </Paper>
        </Box>
    );
}
